import { DataBuilderHttpError } from './errors';

export type JsonLintIssue = {
  /** JSON path to the offending node, e.g. `['deliverables', 0, 'type']`. */
  path: (string | number)[];
  severity: 'error' | 'warning';
  message: string;
};

/**
 * Lightweight structural checks for a pasted Data Builder request — only the
 * fields the loader actually reads (see hydrate-store-from-payload). This is a
 * fast client-side advisory layer; the backend `/verify` endpoint remains the
 * authoritative schema check. JSON syntax errors are intentionally left to
 * CodeMirror's own parse linter.
 */
export const validateDataBuilderJson = (text: string): JsonLintIssue[] => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return [];
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return [
      {
        path: [],
        severity: 'error',
        message: 'The request must be a JSON object.',
      },
    ];
  }

  const deliverables = (parsed as { deliverables?: unknown }).deliverables;
  if (!Array.isArray(deliverables)) {
    return [
      {
        path: ['deliverables'],
        severity: 'error',
        message: 'A Data Builder request needs a "deliverables" array.',
      },
    ];
  }

  const issues: JsonLintIssue[] = [];
  deliverables.forEach((entry, index) => {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) {
      issues.push({
        path: ['deliverables', index],
        severity: 'error',
        message: 'Each deliverable must be an object.',
      });
      return;
    }

    const deliverable = entry as Record<string, unknown>;

    if (
      typeof deliverable.type !== 'string' ||
      deliverable.type.trim() === ''
    ) {
      issues.push({
        path: ['deliverables', index, 'type'],
        severity: 'error',
        message: 'Deliverable is missing a dataset "type".',
      });
    }
    if (!Array.isArray(deliverable.files)) {
      issues.push({
        path: ['deliverables', index, 'files'],
        severity: 'error',
        message: 'Deliverable "files" must be an array.',
      });
    }
    if (
      typeof deliverable.name !== 'string' ||
      deliverable.name.trim() === ''
    ) {
      issues.push({
        path: ['deliverables', index, 'name'],
        severity: 'warning',
        message: 'Deliverable has no "name".',
      });
    }
    for (const field of ['delivery', 'models', 'unit_of_analysis'] as const) {
      if (deliverable[field] === undefined) {
        issues.push({
          path: ['deliverables', index, field],
          severity: 'warning',
          message: `Deliverable is missing "${field}".`,
        });
      }
    }
  });

  return issues;
};

/**
 * Maps a failed `/verify` response (FastAPI's `422` shape,
 * `{ detail: [{ loc, msg }] }`) to inline lint issues. The leading `"body"`
 * segment of each `loc` is dropped so the remaining path lines up with the
 * pasted JSON. Returns `[]` for non-422 errors (handled as a generic message).
 */
export const parseVerifyValidationIssues = (
  error: unknown
): JsonLintIssue[] => {
  if (!(error instanceof DataBuilderHttpError) || error.status !== 422) {
    return [];
  }

  let body: unknown;
  try {
    body = JSON.parse(error.message);
  } catch {
    return [];
  }

  const detail = (body as { detail?: unknown }).detail;
  if (!Array.isArray(detail)) {
    return [];
  }

  return detail.map((entry) => {
    const rawLoc =
      entry !== null &&
      typeof entry === 'object' &&
      Array.isArray((entry as { loc?: unknown }).loc)
        ? (entry as { loc: unknown[] }).loc
        : [];
    const trimmed = rawLoc[0] === 'body' ? rawLoc.slice(1) : rawLoc;
    const path = trimmed.filter(
      (segment): segment is string | number =>
        typeof segment === 'string' || typeof segment === 'number'
    );
    const message =
      entry !== null &&
      typeof entry === 'object' &&
      typeof (entry as { msg?: unknown }).msg === 'string'
        ? (entry as { msg: string }).msg
        : 'Invalid value';
    return { path, severity: 'error' as const, message };
  });
};
